import { Col, Descriptions, Row, Select, Tag } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { formatCurrency, formatDate, getCustomFieldsItems } from "common/utils";
import { UserDefinedFieldValues } from "components/userDefinedFieldValues";
import moment from "moment-timezone";
import styled from "styled-components";

const StyledPanel = styled.div`
  padding: 8px 16px 12px 80px;
  background: #fafafa;
`;

const StyledSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
`;

const StyledTag = styled(Tag)`
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
  cursor: pointer;
`;

const StyledDenseDescriptions = styled(Descriptions)`
  .ant-descriptions-item {
    padding-bottom: 0 !important;
  }
  .ant-descriptions-item-label,
  .ant-descriptions-item-content {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.4 !important;
    font-size: 12px;
    vertical-align: middle;
  }
  .ant-descriptions-item-label {
    width: 115px;
    min-width: 115px;
  }
`;

const StyledTagsContainer = styled.div`
  margin-bottom: 8px;
`;

const StyledForwardingEntityLabel = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  width: 115px;
  min-width: 115px;
  flex-shrink: 0;
`;

const StyledForwardingEntitySelect = styled(Select)`
  flex: 1;
  .ant-select-selector {
    border-bottom: 1px solid #d9d9d9 !important;
    border-radius: 0 !important;
    padding-inline: 0 !important;
  }
  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    font-size: 12px;
  }
`;

const StyledEmptyText = styled.span`
  font-size: 12px;
  color: #bbb;
`;

function AccountRowExpansion({
  record,
  debtorId,
  accountCustomFields,
  forwardingEntities,
  isChangeForwardingAgencyDisabled,
  onChangeForwardingEntity,
  filterOption,
}) {
  const customFieldItems = getCustomFieldsItems(record?.customFields, accountCustomFields);
  const codebtors = record?.debtors?.filter((debtor) => debtor.id.toString() !== debtorId) ?? [];

  const hasAnyTags =
    record?.isClosed ||
    record?.forwardingEntityId !== null ||
    record?.hasNonResolvedDisputes ||
    record?.hasAnyResolvedDisputes ||
    record?.legalStatus;

  return (
    <StyledPanel>
      <Row gutter={24}>
        {/* Section 1: Key Dates */}
        <Col span={8}>
          <StyledSectionTitle>Key Dates</StyledSectionTitle>
          <StyledDenseDescriptions column={1} size="small">
            {record?.dateOfService && (
              <Descriptions.Item label="Service Date">
                {formatDate(record.dateOfService)}
              </Descriptions.Item>
            )}
            {record?.turnoverDate && (
              <Descriptions.Item label="Turnover Date">
                {formatDate(record.turnoverDate)}
              </Descriptions.Item>
            )}
            {record?.itemizationDate && (
              <Descriptions.Item label="Itemization">
                {formatDate(record.itemizationDate)}
              </Descriptions.Item>
            )}
            {record?.interestStartDate && (
              <Descriptions.Item label="Interest Calc.">
                {formatDate(record.interestStartDate)}
              </Descriptions.Item>
            )}
            {record?.statuteOfLimitationsExpiryDate && (
              <Descriptions.Item label="Out of Statute">
                {formatDate(record.statuteOfLimitationsExpiryDate)}
              </Descriptions.Item>
            )}
            {record?.openDate && (
              <Descriptions.Item label="Open">{formatDate(record.openDate)}</Descriptions.Item>
            )}
            {record?.uploadDate && (
              <Descriptions.Item label="Upload">{formatDate(record.uploadDate)}</Descriptions.Item>
            )}
            {record?.dateOfFirstDelinquency && (
              <Descriptions.Item label="1st Delinquency">
                {formatDate(record.dateOfFirstDelinquency)}
              </Descriptions.Item>
            )}
            {record?.chargeOffDate && (
              <Descriptions.Item label="Charge Off">
                {formatDate(record.chargeOffDate)}
              </Descriptions.Item>
            )}
          </StyledDenseDescriptions>
        </Col>

        {/* Section 2: User Defined Fields */}
        <Col span={8}>
          <StyledSectionTitle>User Defined Fields</StyledSectionTitle>
          <Row gutter={0}>
            {customFieldItems?.length > 0 ? (
              <UserDefinedFieldValues customFieldItems={customFieldItems} />
            ) : (
              <StyledEmptyText>None</StyledEmptyText>
            )}
          </Row>
        </Col>

        {/* Section 3: Other */}
        <Col span={8}>
          <StyledSectionTitle>Other</StyledSectionTitle>
          {hasAnyTags && (
            <StyledTagsContainer>
              {record?.isClosed && <StyledTag color="red">closed</StyledTag>}
              {record?.forwardingEntityId !== null && <StyledTag color="blue">forwarded</StyledTag>}
              {record?.hasNonResolvedDisputes && <StyledTag color="red">disputed</StyledTag>}
              {record?.hasAnyResolvedDisputes && (
                <StyledTag color="green">dispute resolved</StyledTag>
              )}
              {record?.statuteOfLimitationsExpiryDate &&
                moment().isAfter(moment(record.statuteOfLimitationsExpiryDate)) && (
                  <StyledTag color="red">out of statute</StyledTag>
                )}
              {record?.legalStatus === "in_litigation" && (
                <StyledTag color="orange">in litigation</StyledTag>
              )}
              {record?.legalStatus === "judgment_applied" && (
                <StyledTag color="green">judgment applied</StyledTag>
              )}
              {record?.legalStatus === "judgment" && (
                <StyledTag color="green">JUDGMENT APPLIED</StyledTag>
              )}
              {record?.legalStatus === "judgment_applied_archived" && (
                <StyledTag color="green">JUDGMENT ARCHIVED</StyledTag>
              )}
            </StyledTagsContainer>
          )}
          <Row align="middle">
            <StyledForwardingEntityLabel>Forwarding Entity:</StyledForwardingEntityLabel>
            <StyledForwardingEntitySelect
              allowClear
              showSearch
              size="small"
              variant="borderless"
              filterOption={filterOption}
              placeholder="Select one..."
              disabled={isChangeForwardingAgencyDisabled(record)}
              popupMatchSelectWidth={false}
              value={record?.forwardingEntityId ?? undefined}
              onChange={async (value) => {
                if (value !== record?.forwardingEntityId) {
                  await onChangeForwardingEntity({
                    selectedAccountId: record.id,
                    forwardingEntityId: value ?? null,
                  });
                }
              }}
              options={
                forwardingEntities?.map((fe) => ({
                  label: fe.name,
                  value: fe.id,
                })) ?? []
              }
            />
          </Row>
          <StyledDenseDescriptions column={1} size="small">
            <Descriptions.Item label="Last Pmt Amt">
              {record?.lastPaymentAmount != null && parseFloat(record.lastPaymentAmount) !== 0
                ? formatCurrency(record.lastPaymentAmount)
                : "N/A"}
            </Descriptions.Item>

            {record?.lastPaymentDate && (
              <Descriptions.Item label="Last Pmt Date">
                {formatDate(record.lastPaymentDate)}
              </Descriptions.Item>
            )}
            {record?.externalScore != null && (
              <Descriptions.Item label="Experian Score">{record.externalScore}</Descriptions.Item>
            )}
            {codebtors.map((codebtor) => (
              <Descriptions.Item key={codebtor.id} label="Co-Debtor">
                <a href={`/debtors/${codebtor.id}`}>
                  {codebtor.firstName} {codebtor.middleName} {codebtor.lastName} ({codebtor.id})
                </a>
              </Descriptions.Item>
            ))}
          </StyledDenseDescriptions>
        </Col>
      </Row>
    </StyledPanel>
  );
}

export default AccountRowExpansion;
